import { useState, useCallback } from 'react';
import Tesseract, { createWorker } from 'tesseract.js';

export interface OcrResult {
  text: string;
  confidence: number;
  extractedData: {
    invoiceNumber?: string;
    issueDate?: string;
    totalGross?: string;
    sellerName?: string;
    buyerName?: string;
  };
}

export interface OcrProgress {
  status: string;
  progress: number; // 0-100
}

interface UseOcrProcessingReturn {
  processImage: (file: File) => Promise<OcrResult>;
  isProcessing: boolean;
  progress: OcrProgress | null;
  error: string | null;
  cancelProcessing: () => void;
}

/**
 * Hook for processing images with Tesseract.js OCR
 * Extracts text and attempts to parse invoice data
 */
export const useOcrProcessing = (): UseOcrProcessingReturn => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<OcrProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [worker, setWorker] = useState<Tesseract.Worker | null>(null);

  const cancelProcessing = useCallback(() => {
    if (worker) {
      worker.terminate();
      setWorker(null);
      setIsProcessing(false);
      setProgress(null);
    }
  }, [worker]);

  const extractInvoiceData = useCallback((text: string) => {
    const extractedData: OcrResult['extractedData'] = {};

    // Extract invoice number (various patterns)
    const invoiceNumberPatterns = [
      /(?:faktura|invoice|nr|no|number)[:\s#]*([A-Z0-9/-]+)/i,
      /(?:FV|FA|FVZ)[:\s#]*([0-9]+\/[0-9]+\/[0-9]+)/i,
      /nr[\s.]*([0-9]+\/[0-9]+)/i,
    ];
    for (const pattern of invoiceNumberPatterns) {
      const match = text.match(pattern);
      if (match) {
        extractedData.invoiceNumber = match[1].trim();
        break;
      }
    }

    // Extract issue date (various formats)
    const datePatterns = [
      /(?:data|date|wystawienia|issue)[:\s]*(\d{2}[.-]\d{2}[.-]\d{4})/i,
      /(?:data|date|wystawienia|issue)[:\s]*(\d{4}[.-]\d{2}[.-]\d{2})/i,
      /(\d{2}[.-]\d{2}[.-]\d{4})/,
    ];
    for (const pattern of datePatterns) {
      const match = text.match(pattern);
      if (match) {
        extractedData.issueDate = match[1].replace(/\./g, '-');
        break;
      }
    }

    // Extract total gross (with PLN, zł, EUR, etc.)
    const totalPatterns = [
      /(?:suma|total|razem|do zapłaty|to pay)[:\s]*([0-9\s,.']+)\s*(?:PLN|zł|EUR|USD)?/i,
      /(?:brutto|gross)[:\s]*([0-9\s,.']+)\s*(?:PLN|zł|EUR|USD)?/i,
    ];
    for (const pattern of totalPatterns) {
      const match = text.match(pattern);
      if (match) {
        // Clean amount: remove spaces, replace comma with dot
        const cleanAmount = match[1].replace(/\s/g, '').replace(',', '.');
        extractedData.totalGross = cleanAmount;
        break;
      }
    }

    // Extract seller name (after "Sprzedawca" or "Seller")
    const sellerPattern = /(?:sprzedawca|seller|from)[:\s]*([^\n]+)/i;
    const sellerMatch = text.match(sellerPattern);
    if (sellerMatch) {
      extractedData.sellerName = sellerMatch[1].trim();
    }

    // Extract buyer name (after "Nabywca" or "Buyer")
    const buyerPattern = /(?:nabywca|buyer|to)[:\s]*([^\n]+)/i;
    const buyerMatch = text.match(buyerPattern);
    if (buyerMatch) {
      extractedData.buyerName = buyerMatch[1].trim();
    }

    return extractedData;
  }, []);

  const processImage = useCallback(
    async (file: File): Promise<OcrResult> => {
      setIsProcessing(true);
      setError(null);
      setProgress({ status: 'Inicjalizacja OCR...', progress: 0 });

      try {
        // Create Tesseract worker
        const ocrWorker = await createWorker('pol', 1, {
          logger: (m) => {
            if (m.status === 'recognizing text') {
              setProgress({
                status: 'Rozpoznawanie tekstu...',
                progress: Math.round(m.progress * 100),
              });
            } else if (m.status === 'loading tesseract core') {
              setProgress({
                status: 'Ładowanie silnika OCR...',
                progress: Math.round(m.progress * 50),
              });
            } else if (m.status === 'initializing tesseract') {
              setProgress({
                status: 'Inicjalizacja...',
                progress: Math.round(50 + m.progress * 25),
              });
            } else if (m.status === 'loading language traineddata') {
              setProgress({
                status: 'Ładowanie słownika polskiego...',
                progress: Math.round(75 + m.progress * 25),
              });
            }
          },
        });

        setWorker(ocrWorker);

        // Perform OCR
        const {
          data: { text, confidence },
        } = await ocrWorker.recognize(file);

        // Extract structured invoice data from text
        const extractedData = extractInvoiceData(text);

        // Terminate worker
        await ocrWorker.terminate();
        setWorker(null);

        setIsProcessing(false);
        setProgress({ status: 'Zakończono', progress: 100 });

        return {
          text,
          confidence,
          extractedData,
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Nieznany błąd podczas przetwarzania OCR';
        setError(errorMessage);
        setIsProcessing(false);
        setProgress(null);

        if (worker) {
          await worker.terminate();
          setWorker(null);
        }

        throw new Error(errorMessage);
      }
    },
    [extractInvoiceData, worker]
  );

  return {
    processImage,
    isProcessing,
    progress,
    error,
    cancelProcessing,
  };
};
